import { useCallback, useEffect, useMemo, useState } from 'react';
import { TranslatedString, TranslationFunction } from '../../components/blocks/localization';
import {
    AsynchronouslyLoadedValueStatus,
    IAsynchronouslyLoadedValue,
    PoReviewDraft,
    ResellerDetails,
    ResellerItem,
    ResellerPriceList,
} from '../../redux/types';
import { CodedError } from '../../types';
import { Logger } from '../../utils/logger';

const EXTRACTION_NOT_REVIEWABLE = 'ExtractionNotReviewable';

type UsePoReviewDataArgs = {
    resellerId: string;
    extractionId: string;
    draft: IAsynchronouslyLoadedValue<PoReviewDraft>;
    resellerDetails: IAsynchronouslyLoadedValue<ResellerDetails>;
    resellerItems: IAsynchronouslyLoadedValue<ResellerItem[]>;
    resellerPriceList: IAsynchronouslyLoadedValue<ResellerPriceList>;
    unmappedItemStatus: AsynchronouslyLoadedValueStatus;
    loadPoReviewDraft: (resellerId: string, extractionId: string) => void;
    getResellerDetails: (resellerId: string) => void;
    getResellerItems: (resellerId: string) => void;
    getResellerPriceList: (resellerId: string) => void;
    getUnmappedItems: (resellerId: string, itemIds: string[]) => void;
    t: TranslationFunction;
    showError: (message: TranslatedString) => void;
};

type UsePoReviewDataResult = {
    draft: IAsynchronouslyLoadedValue<PoReviewDraft>;
    resellerDetails: IAsynchronouslyLoadedValue<ResellerDetails>;
    resellerItems: IAsynchronouslyLoadedValue<ResellerItem[]>;
    resellerPriceList: IAsynchronouslyLoadedValue<ResellerPriceList>;
    itemMap: Map<string, ResellerItem>;
};

type LoadAndReportArgs<T> = {
    loadedValue: IAsynchronouslyLoadedValue<T>;
    load: () => void;
    failureMessage: TranslatedString;
    description: string;
    showError: (message: TranslatedString) => void;
};

function useLoadAndReport<T>({ loadedValue, load, failureMessage, description, showError }: LoadAndReportArgs<T>) {
    const [hasErrorBeenReported, setHasErrorBeenReported] = useState(false);

    useEffect(() => {
        switch (loadedValue.status) {
            case 'Uninitialized':
                setHasErrorBeenReported(false);
                load();
                break;
            case 'Failed':
                Logger.debug(`failed to load ${description}: ${loadedValue.error}`);
                if (!hasErrorBeenReported) {
                    showError(failureMessage);
                    setHasErrorBeenReported(true);
                }
                break;
            default:
            // do nothing
        }
    }, [description, failureMessage, hasErrorBeenReported, load, loadedValue, showError]);
}

// The item ids the select must offer beyond the reseller's mapped list, the ids on the extracted lines
// and on the price list.
function unmappedItemIds(
    resellerItems: IAsynchronouslyLoadedValue<ResellerItem[]>,
    resellerPriceList: IAsynchronouslyLoadedValue<ResellerPriceList>,
    unmappedItemStatus: AsynchronouslyLoadedValueStatus,
    draft: IAsynchronouslyLoadedValue<PoReviewDraft>,
    itemMap: Map<string, ResellerItem>
): string[] {
    if (
        draft.status !== 'Loaded' ||
        resellerItems.status !== 'Loaded' ||
        resellerPriceList.status !== 'Loaded' ||
        unmappedItemStatus !== 'Uninitialized'
    ) {
        return [];
    }
    const rowItemIds = (draft.value?.rows ?? [])
        .map((row) => row.itemId)
        .filter((itemId): itemId is string => itemId !== undefined && itemId !== null);
    const priceListItemIds = (resellerPriceList.value?.priceListItems ?? []).map((item) => item.itemId);
    return [...new Set([...rowItemIds, ...priceListItemIds])].filter((itemId) => !itemMap.has(itemId));
}

// Loads the review page's four resources, reporting each failure once, fetches any item the select must
// offer that the mapped list lacks, and returns the loaded values plus an itemId to item map.
export function usePoReviewData({
    resellerId,
    extractionId,
    draft,
    resellerDetails,
    resellerItems,
    resellerPriceList,
    unmappedItemStatus,
    loadPoReviewDraft,
    getResellerDetails,
    getResellerItems,
    getResellerPriceList,
    getUnmappedItems,
    t,
    showError,
}: UsePoReviewDataArgs): UsePoReviewDataResult {
    const [hasUnmappedItemsErrorBeenReported, setHasUnmappedItemsErrorBeenReported] = useState(false);

    const itemMap = useMemo(
        () => new Map<string, ResellerItem>((resellerItems.value ?? []).map((item) => [item.itemId, item])),
        [resellerItems.value]
    );

    const loadDraft = useCallback(() => loadPoReviewDraft(resellerId, extractionId), [
        extractionId,
        loadPoReviewDraft,
        resellerId,
    ]);
    const loadResellerDetails = useCallback(() => getResellerDetails(resellerId), [getResellerDetails, resellerId]);
    const loadResellerItems = useCallback(() => getResellerItems(resellerId), [getResellerItems, resellerId]);
    const loadResellerPriceList = useCallback(() => getResellerPriceList(resellerId), [
        getResellerPriceList,
        resellerId,
    ]);

    useLoadAndReport({
        loadedValue: draft,
        load: loadDraft,
        failureMessage:
            (draft.error as CodedError)?.code === EXTRACTION_NOT_REVIEWABLE
                ? t('extractionNotReviewable-errorMessage')
                : t('failedToLoadExtraction-errorMessage'),
        description: `purchase order extraction ${extractionId}`,
        showError,
    });

    useLoadAndReport({
        loadedValue: resellerDetails,
        load: loadResellerDetails,
        failureMessage: t('failedToLoadResellerDetails-errorMessage'),
        description: 'reseller details',
        showError,
    });

    useLoadAndReport({
        loadedValue: resellerItems,
        load: loadResellerItems,
        failureMessage: t('failedToLoadResellerItems-errorMessage'),
        description: 'reseller items',
        showError,
    });

    useLoadAndReport({
        loadedValue: resellerPriceList,
        load: loadResellerPriceList,
        failureMessage: t('failedToLoadResellerPriceList-errorMessage'),
        description: 'reseller pricelist',
        showError,
    });

    useEffect(() => {
        if (unmappedItemStatus === 'Failed') {
            Logger.debug('failed to load unmapped items');
            if (!hasUnmappedItemsErrorBeenReported) {
                showError(t('failedToLoadResellerItems-errorMessage'));
                setHasUnmappedItemsErrorBeenReported(true);
            }
            return;
        }
        const ids = unmappedItemIds(resellerItems, resellerPriceList, unmappedItemStatus, draft, itemMap);
        if (ids.length > 0) {
            setHasUnmappedItemsErrorBeenReported(false);
            getUnmappedItems(resellerId, ids);
        }
    }, [
        draft,
        getUnmappedItems,
        hasUnmappedItemsErrorBeenReported,
        itemMap,
        resellerId,
        resellerItems,
        resellerPriceList,
        showError,
        t,
        unmappedItemStatus,
    ]);

    return { draft, resellerDetails, resellerItems, resellerPriceList, itemMap };
}
